import React, { useState, useEffect, useRef } from 'react';
import {
  Overlay, CenterContainer, RightContainer, CreateSplitLayout, ParentSide, ParentCard,
  ArrowDivider, InputSide, ParentLabel, ParentTitle, ParentDesc, ModalBox, Header, HeaderTitle,
  IconBtn, CloseBtn, Body, NodeSection, BottomBar, NodeTitle, NodeDescription, HumanText, AIText,
  TextSeparator, Spinner, EditTextArea, InputWrapper, FloatingActions, FloatingActionsGroup,
  CreateTextArea, ButtonRow, Btn, CommentBox, CommentHeader, CommentName, CommentDate, CommentText,
  Label, SectionTitle, ValenceRow, ValenceLabel, ValenceBtn, ValenceSource, VoteGroup, VoteBtn,
  NoteForm,
} from './ImpactPathNodeModal.styles';
import { X, Pencil, Save, Trash2, Plus, ArrowRight, ThumbsUp, ThumbsDown } from 'lucide-react';
import { useTranslation } from '../../hooks/useTranslation.jsx';
import { useNotice } from '../../components/DialogProvider';
import { diffWords } from 'diff';
import AIScorePanel from '../../components/AIScorePanel';
import { extractTagEvaluation, hasLegacyNumericScores } from '../../utils/evaluationDisplay';
import DeleteConfirmButton from '../../components/DeleteConfirmButton';
import UndoRedoButtons from '../../components/UndoRedoButtons';
import TidyTextButton from '../../components/TidyTextButton';
import useTextHistory from '../../hooks/useTextHistory';
import { useAiUnread } from '../../contexts/AiUnreadContext';
import { hasNodeAiAnalysis } from '../../utils/aiAnalysisPresence';
import { FormCheckOwnIndicator } from '../../components/InputFeedbackIndicator';
import { VALENCE_KEYS, VALENCE_STYLE } from '../../config/valence';
import ProposedNodePanel from './ProposedNodePanel';

// --- Header ---

// --- Content area ---

// --- View mode styled ---

// --- Edit mode ---


// --- Create mode ---

// --- Buttons ---

// --- Delete confirmation ---

// --- Comments ---

// ==========================
// Description diff rendering
// ==========================

function renderDescriptionDiff(node, displayText) {
  const humanText = node?.human_text || '';
  const aiText = node?.ai_analysis || '';
  const currentDesc = node?.description || node?.fullText || '';

  if (!humanText && !aiText) return displayText;

  const originalCombined = humanText && aiText
    ? `${humanText} – ${aiText}`
    : humanText || aiText;

  if (currentDesc === originalCombined || !currentDesc) {
    return (
      <>
        {humanText && <HumanText>{humanText}</HumanText>}
        {humanText && aiText && <TextSeparator>–</TextSeparator>}
        {aiText && <AIText>{aiText}</AIText>}
      </>
    );
  }

  const separator = ' – ';
  const humanEnd = humanText.length;
  const separatorStart = humanEnd;
  const separatorEnd = humanText && aiText ? humanEnd + separator.length : humanEnd;
  const changes = diffWords(originalCombined, currentDesc);

  let origPos = 0;
  return changes.map((part, index) => {
    if (part.added) return <HumanText key={index}>{part.value}</HumanText>;
    if (part.removed) { origPos += part.value.length; return null; }

    const partStart = origPos;
    const partEnd = origPos + part.value.length;
    origPos = partEnd;

    if (partEnd <= humanEnd) return <HumanText key={index}>{part.value}</HumanText>;
    if (partStart >= separatorEnd) return <AIText key={index}>{part.value}</AIText>;
    if (partStart >= separatorStart && partEnd <= separatorEnd) return <TextSeparator key={index}>{part.value}</TextSeparator>;

    // Spans regions
    const segments = [];
    let segStart = partStart;
    let textOffset = 0;

    if (segStart < humanEnd) {
      const len = Math.min(partEnd, humanEnd) - segStart;
      segments.push(<HumanText key={`${index}-h`}>{part.value.substring(textOffset, textOffset + len)}</HumanText>);
      textOffset += len;
      segStart += len;
    }
    if (segStart < separatorEnd && segStart >= separatorStart) {
      const len = Math.min(partEnd, separatorEnd) - segStart;
      segments.push(<TextSeparator key={`${index}-s`}>{part.value.substring(textOffset, textOffset + len)}</TextSeparator>);
      textOffset += len;
      segStart += len;
    }
    if (segStart >= separatorEnd && textOffset < part.value.length) {
      segments.push(<AIText key={`${index}-a`}>{part.value.substring(textOffset)}</AIText>);
    }
    return segments;
  });
}

// ==========================
// Truncation helper
// ==========================

function truncateText(text, wordLimit = 60) {
  if (!text) return '';
  const words = text.trim().split(/\s+/);
  if (words.length <= wordLimit) return text;
  return words.slice(0, wordLimit).join(' ') + '...';
}

// ==========================
// Main component
// ==========================

/** ImpactPathNodeModal — Unified modal for view/edit/create on Impact Path nodes. */
/* Valence — three states a person can set, one row, under the header. The
   fourth visual state (nothing selected) is "not judged", which is not a
   button: the map should not claim an opinion the room never gave. */
export default function ImpactPathNodeModal({
  isOpen,
  mode: initialMode = 'view',
  position = 'center', width = null, // width: narrower when the neighbourhood frames it (NodeNeighbourhood)
  node,
  parentNode,
  instanceId,
  isCreator = false,
  isAdmin = false,
  hasOthersChildren = 0,
  onClose,
  onSave,
  onAdd,
  onDelete,
  onAddConsequence,
  onAnalyzeNode,
  /** When user did not manually edit the title field, regenerate headline after description change. */
  onGenerateTitle,
  onComment,
  comments = [],
  childCount = 0,
  formCheckPending = false,
  formCheckDismissed = false,
  onDismissFormCheck,
  /** Optional pill naming what kind of object this is — "B1 · Tasapainottava
   *  silmukka". Nodes need none; blockers and loops open in this same modal and
   *  would otherwise read as ordinary nodes.
   *  { text, color, options?: [{ key, label, hint, checked, onSelect }] } */
  entityBadge = null,
  // Valence (§4b). Rendered only when the caller can save it — v1 passes
  // nothing and sees nothing. `valence` null = nobody has judged this node.
  valence = null,
  valenceSource = null,
  onValenceChange = null,
  // §4h — three ways to react to a card. Votes say "does this matter";
  // comments are notes about the node, never nodes.
  votes = null,            // { up, down, mine: 'agree' | 'disagree' | null } or null to hide
  onVote = null,           // (type) => void
  proposal = null,         // ProposedNodePanel props: a card drafted from the team's documents, or its provenance once decided
}) {
  const { t, language } = useTranslation();
  const notice = useNotice();
  const [noteDraft, setNoteDraft] = useState('');
  const [notePending, setNotePending] = useState(false);
  const { markRead } = useAiUnread();
  // --- Mode state ---
  const [mode, setMode] = useState(initialMode);

  // --- View/Edit state ---
  const [descExpanded, setDescExpanded] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);

  // Edit fields
  const [editTitle, setEditTitle] = useState('');
  const [editDesc, setEditDesc] = useState('');
  const [isSaving, setIsSaving] = useState(false);
  const titleFieldDirtyRef = useRef(false);
  const initialEditDescRef = useRef('');
  const createTextAreaRef = useRef(null);

  // --- Create state ---
  const [createText, setCreateText] = useState('');

  // Grow the box with the text instead of scrolling a fixed-height field.
  useEffect(() => {
    const el = createTextAreaRef.current;
    if (!el) return;
    el.style.height = 'auto';
    el.style.height = `${el.scrollHeight}px`;
  }, [createText]);

  const [isAddLoading, setIsAddLoading] = useState(false);
  const [aiSources, setAiSources] = useState([]);
  const [aiScores, setAiScores] = useState(null);
  const [isAnalyzing, setIsAnalyzing] = useState(false);
  const [analysisReady, setAnalysisReady] = useState(false);
  const {
    canUndo: canUndoCreate,
    canRedo: canRedoCreate,
    pushSnapshot: pushCreateSnapshot,
    undo: undoCreate,
    redo: redoCreate,
    setCurrentValue: setCurrentCreateValue,
  } = useTextHistory(setCreateText);
  const {
    canUndo: canUndoEdit,
    canRedo: canRedoEdit,
    pushSnapshot: pushEditSnapshot,
    undo: undoEdit,
    redo: redoEdit,
    setCurrentValue: setCurrentEditValue,
  } = useTextHistory(setEditDesc);

  // Sync mode when prop changes
  useEffect(() => { setMode(initialMode); }, [initialMode]);

  // Mark AI evaluation as read when user opens view/edit modal with scores
  useEffect(() => {
    if (!isOpen || !node?.id) return;
    if ((mode === 'view' || mode === 'edit') && hasNodeAiAnalysis(node.node_analysis)) {
      markRead('node', node.id);
    }
  }, [isOpen, node?.id, node?.node_analysis, mode, markRead]);

  // When modal opens with a node, sync edit fields and reset "user owns title" tracking
  useEffect(() => {
    if (!isOpen || !node?.id) return;
    setEditTitle(node.title || node.text || '');
    setEditDesc(node.fullText || node.description || '');
    initialEditDescRef.current = (node.fullText || node.description || '').trim();
    titleFieldDirtyRef.current = false;
  }, [node?.id, isOpen]);

  // Reset create state when modal opens/closes
  useEffect(() => {
    if (!isOpen) {
      setCreateText('');
      setAiSources([]);
      setAiScores(null);
      setIsAnalyzing(false);
      setAnalysisReady(false);
      setShowDeleteConfirm(false);
      setIsDeleting(false);
    }
  }, [isOpen]);

  useEffect(() => {
    setCurrentCreateValue(createText);
  }, [createText, setCurrentCreateValue]);

  useEffect(() => {
    setCurrentEditValue(editDesc);
  }, [editDesc, setCurrentEditValue]);

  // Escape key to close
  useEffect(() => {
    if (!isOpen) return;
    const handleEscape = (e) => { if (e.key === 'Escape') onClose?.(); };
    window.addEventListener('keydown', handleEscape);
    return () => window.removeEventListener('keydown', handleEscape);
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  // --- Resolve references/sources ---
  // Prefer node_analysis references (from AI analysis), fall back to ai_sources
  const nodeRefs = node?.node_analysis?.references || node?.references || node?.ai_sources || [];
  const nodeSources = node?.sources || node?.metadata?.originalData?.sources || [];
  // References can be objects { citation, relevance, excerpt } or plain strings
  const allRefs = nodeRefs.map(r =>
    typeof r === 'string' ? { citation: r } : r
  );
  // Split sources: cited in text vs used only for analysis
  const nodeTextForRefs = node?.ai_analysis || node?.description || node?.fullText || '';
  const citedRefs = [];
  const analysisRefs = [];
  allRefs.forEach((ref, i) => {
    const marker = ref.marker || `[${i + 1}]`;
    if (nodeTextForRefs.includes(marker)) {
      citedRefs.push(ref);
    } else {
      analysisRefs.push(ref);
    }
  });
  // Combined: cited first, then analysis with type marker
  const displayRefs = [
    ...citedRefs,
    ...analysisRefs.map(r => ({ ...r, type: 'analysis' })),
  ];

  // Scores from node data (stored in node_analysis.scores, rag_references.scores, or directly)
  const nodeEval = extractTagEvaluation(node?.node_analysis) || extractTagEvaluation(node?.rag_references?.scores);
  const nodeLegacyScores = !nodeEval && hasLegacyNumericScores(node?.node_analysis?.scores || node?.scores || node?.rag_references?.scores)
    ? (node?.node_analysis?.scores || node?.scores || node?.rag_references?.scores)
    : null;

  // --- Handlers ---

  const handleStartEdit = () => {
    setEditTitle(node?.title || node?.text || '');
    setEditDesc(node?.fullText || node?.description || '');
    initialEditDescRef.current = (node?.fullText || node?.description || '').trim();
    titleFieldDirtyRef.current = false;
    setAiScores(null);
    setAiSources([]);
    setAnalysisReady(false);
    setIsAnalyzing(false);
    setMode('edit');
  };

  const handleCancelEdit = () => {
    setMode('view');
    setEditTitle(node?.title || node?.text || '');
    setEditDesc(node?.fullText || node?.description || '');
    setAiScores(null);
    setAiSources([]);
    setAnalysisReady(false);
  };

  const handleSaveEdit = async () => {
    if (!onSave || !node?.id || isSaving || isAnalyzing) return;
    const currentText = editDesc.trim();
    const skipAnalysis = node?.is_scenario_starter === true;

    // Run analysis first if not yet done (not for muuttujat — vague factors).
    if (!skipAnalysis && !analysisReady && onAnalyzeNode && currentText) {
      setIsAnalyzing(true);
      try {
        const result = await onAnalyzeNode(currentText);
        if (result?.tags) setAiScores(result);
        else if (result?.scores) setAiScores(result.scores);
        if (result?.references) setAiSources(result.references);
        setAnalysisReady(true);
      } catch (err) {
        console.error('Node analysis failed:', err);
        setAnalysisReady(true);
      } finally {
        setIsAnalyzing(false);
      }
      return; // Show results first
    }

    setIsSaving(true);
    try {
      let titleOut = editTitle.trim() || currentText.split(/[.!?\n]/)[0].substring(0, 80);
      const descChanged = currentText !== initialEditDescRef.current;
      if (!titleFieldDirtyRef.current && descChanged && onGenerateTitle) {
        try {
          const generated = await onGenerateTitle(currentText);
          if (generated && typeof generated === 'string' && generated.trim()) {
            titleOut = generated.trim().substring(0, 80);
          }
        } catch (e) {
          console.warn('ImpactPathNodeModal: title regeneration skipped', e);
        }
      }
      const nodeAnalysis = aiScores
        ? (aiScores.tags ? { ...aiScores, references: aiSources } : { scores: aiScores, references: aiSources })
        : null;
      const payload = { id: node.id, title: titleOut, description: currentText };
      if (nodeAnalysis) payload.node_analysis = nodeAnalysis;
      await onSave(payload);
      setAnalysisReady(false);
      onClose?.();
    } catch (err) {
      console.error('Failed to save node:', err);
      notice.error(t('impactPath.nodeModal.saveFailed'));
    } finally {
      setIsSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!onDelete || !node?.id) return;
    setIsDeleting(true);
    try {
      await onDelete(node.id);
      onClose?.();
    } catch (err) {
      console.error('Failed to delete node:', err);
      notice.error(t('impactPath.nodeModal.deleteFailed'));
    } finally {
      setIsDeleting(false);
    }
  };

  const handleAdd = async () => {
    if (!createText.trim() || isAddLoading || isAnalyzing) return;

    const currentText = createText.trim();
    // Human nodes save immediately — backend runs analysis fire-and-forget,
    // tree node shows glimmer until scores arrive via socket

    setIsAddLoading(true);
    try {
      const nodeAnalysis = aiScores
        ? (aiScores.tags ? { ...aiScores, references: finalSources } : { scores: aiScores, references: finalSources })
        : null;

      onAdd?.({
        title: currentText,
        description: currentText,
        human_text: currentText,
        node_analysis: nodeAnalysis,
      });

      setCreateText('');
      setAiSources([]);
      setAiScores(null);
      setAnalysisReady(false);
    } finally {
      setIsAddLoading(false);
    }
  };

  const handleCreateKeyDown = (e) => {
    if ((e.metaKey || e.ctrlKey) && e.key === 'Enter') { e.preventDefault(); handleAdd(); }
    if (e.key === 'Escape') onClose?.();
  };

  // --- Description truncation ---
  const descText = node?.fullText || node?.description || '';
  const isTruncated = descText.trim().split(/\s+/).length > 60;
  const displayDesc = descExpanded ? descText : truncateText(descText);

  // Only show sources actually cited in the description text
  const descForMarkers = createText || '';
  const citedSources = aiSources.filter((s, i) => {
    const marker = s.marker || `[${i + 1}]`;
    return descForMarkers.includes(marker);
  });
  // Fallback: if no markers found (e.g. view mode with no description), show all
  const finalSources = citedSources.length > 0 ? citedSources : aiSources;

  // --- Render ---

  const isRight = position === 'right';
  const Container = (isRight && mode !== 'create') ? RightContainer : CenterContainer;
  const overlayDim = isRight ? 0.5 : 0.8;

  return (
    <>
      <Overlay $dim={overlayDim} />
      <Container>
        <ModalBox
          $width={width || (mode === 'create' && parentNode ? '1100px' : isRight ? '700px' : '800px')}
          $accent={mode === 'create'}
          onClick={(e) => e.stopPropagation()}
        >
          {/* ========== CREATE MODE ========== */}
          {mode === 'create' && (
            <>
              <Header $create>
                <HeaderTitle>{t('impactPath.nodeModal.headerTitle')}</HeaderTitle>
                <CloseBtn onClick={onClose}><X /></CloseBtn>
              </Header>
              <CreateSplitLayout>
                {parentNode && (
                  <ParentSide>
                    <ParentCard>
                      <ParentLabel>{t('impactPath.nodeModal.parentLabel')}</ParentLabel>
                      <ParentTitle>{parentNode.title || parentNode.text}</ParentTitle>
                      {parentNode.description && parentNode.description !== (parentNode.title || parentNode.text) && (
                        <ParentDesc>{parentNode.description.length > 300 ? parentNode.description.substring(0, 300) + '...' : parentNode.description}</ParentDesc>
                      )}
                    </ParentCard>
                    <ArrowDivider><ArrowRight size={24} /></ArrowDivider>
                  </ParentSide>
                )}
                <InputSide>
                  <div>
                    <Label>{t('impactPath.nodeModal.describeConsequenceLabel')}</Label>
                    <InputWrapper>
                      <CreateTextArea
                        ref={createTextAreaRef}
                        value={createText}
                        onChange={(e) => { setCreateText(e.target.value); if (analysisReady) { setAnalysisReady(false); setAiScores(null); setAiSources([]); } }}
                        onKeyDown={handleCreateKeyDown}
                        placeholder={t('impactPath.nodeModal.consequencePlaceholder')}
                        autoFocus
                      />
                      <FloatingActions>
                        <FloatingActionsGroup>
                          <UndoRedoButtons
                            canUndo={canUndoCreate}
                            canRedo={canRedoCreate}
                            onUndo={undoCreate}
                            onRedo={redoCreate}
                            buttonComponent={Btn}
                            buttonStyle={{
                              padding: '8px 10px',
                              fontSize: '0.78rem',
                              fontWeight: 600,
                              // Opaque, not floating over live text — undo was
                              // hard to read as a transparent button on top of text.
                              background: '#2a2a2a',
                              border: '1px solid #555',
                              color: '#fff',
                            }}
                          />
                          <TidyTextButton
                            text={createText}
                            instanceId={instanceId}
                            onApply={setCreateText}
                            onBeforeApply={pushCreateSnapshot}
                            buttonComponent={Btn}
                            buttonStyle={{
                              padding: '8px 10px',
                              fontSize: '0.78rem',
                              fontWeight: 600,
                              background: '#2a2a2a',
                              border: '1px solid #555',
                              color: '#fff',
                            }}
                          />
                        </FloatingActionsGroup>
                      </FloatingActions>
                    </InputWrapper>
                  </div>

                  {isAnalyzing && (
                    <div style={{
                      display: 'flex', alignItems: 'center', gap: 8,
                      color: 'rgba(255,255,255,0.5)', fontSize: '0.85rem', padding: '12px 0',
                    }}>
                      <Spinner size={16} />
                      {t('impactPath.nodeModal.aiAnalyzingThought')}
                    </div>
                  )}

                  {(aiScores || finalSources?.length > 0) && (
                    <AIScorePanel evaluation={aiScores} scores={aiScores?.tags ? null : aiScores} sources={finalSources} />
                  )}

                  <ButtonRow $mt="8px" $justify="flex-end">
                    {analysisReady && (
                      <Btn onClick={() => { setAiScores(null); setAiSources([]); setAnalysisReady(false); }}>
                        {t('impactPath.nodeModal.editAdjust')}
                      </Btn>
                    )}
                    <Btn $variant="primary" onClick={handleAdd} disabled={!createText.trim() || isAddLoading || isAnalyzing}>
                      {isAddLoading ? t('impactPath.nodeModal.creating') : isAnalyzing ? t('impactPath.nodeModal.analyzing') : t('impactPath.nodeModal.continue')}
                    </Btn>
                  </ButtonRow>
                </InputSide>
              </CreateSplitLayout>
            </>
          )}

          {/* ========== VIEW / EDIT MODE ========== */}
          {(mode === 'view' || mode === 'edit') && (
            <>
              {/* Top bar: Edit + Close */}
              <Header $badged={Boolean(entityBadge)}>
                {entityBadge ? (
                  <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                    <span
                      style={{
                        display: 'inline-flex', alignItems: 'center', gap: 8,
                        padding: '4px 12px', borderRadius: 999,
                        fontSize: '0.76rem', fontWeight: 700, letterSpacing: '0.04em',
                        textTransform: 'uppercase',
                        color: entityBadge.color,
                        background: `${entityBadge.color}1f`,
                        border: `1px solid ${entityBadge.color}66`,
                      }}
                    >
                      {entityBadge.text}
                    </span>
                    {/* Some things about an object are the room's call rather
                        than the data's — which way a spiral runs, for one. */}
                    {(entityBadge.options || []).map((opt) => (
                      <button
                        key={opt.key}
                        type="button"
                        title={opt.hint}
                        onClick={opt.onSelect}
                        style={{
                          padding: '4px 11px',
                          borderRadius: 999,
                          fontSize: '0.74rem',
                          fontWeight: 600,
                          fontFamily: 'inherit',
                          cursor: 'pointer',
                          color: opt.checked ? '#fff' : 'rgba(255,255,255,0.55)',
                          background: opt.checked ? 'rgba(255,255,255,0.12)' : 'transparent',
                          border: `1px solid ${opt.checked ? 'rgba(255,255,255,0.3)' : 'rgba(255,255,255,0.14)'}`,
                        }}
                      >
                        {opt.checked ? '✓ ' : ''}{opt.label}
                      </button>
                    ))}
                  </span>
                ) : <span />}
                <div style={{ display: 'flex', gap: 8 }}>
                  {mode === 'view' && (isCreator || isAdmin) && onSave && (
                    <IconBtn onClick={handleStartEdit} title={t('impactPath.nodeModal.editButtonTitle')}><Pencil /></IconBtn>
                  )}
                  <IconBtn onClick={onClose} title={t('impactPath.nodeModal.closeTitle')}><X /></IconBtn>
                </div>
              </Header>
              {entityBadge?.ring && (
                <div style={{ margin: '4px 0 0', padding: '0 32px 0 24px', fontSize: '0.86rem', lineHeight: 1.5, color: 'rgba(255,255,255,0.62)' }}>
                  {entityBadge.ring}
                </div>
              )}

              {onValenceChange && (
                <ValenceRow>
                  <ValenceLabel>{t('impactPath.valence.label')}</ValenceLabel>
                  {VALENCE_KEYS.map((key) => {
                    const vs = VALENCE_STYLE[key];
                    const on = valence === key;
                    return (
                      <ValenceBtn
                        key={key}
                        type="button"
                        $on={on}
                        $color={vs.color}
                        $bg={vs.tint}
                        aria-pressed={on}
                        onClick={() => onValenceChange(on ? null : key)}
                      >
                        {vs.label}
                      </ValenceBtn>
                    );
                  })}
                  {valence && valenceSource === 'ai' && (
                    <ValenceSource>{t('impactPath.valence.byAi')}</ValenceSource>
                  )}
                  {votes && onVote && (
                    <VoteGroup title={t('impactPath.votes.title')}>
                      <VoteBtn type="button" $on={votes.mine === 'agree'} aria-pressed={votes.mine === 'agree'} onClick={() => onVote('agree')}>
                        <ThumbsUp size={13} /> {votes.up || 0}
                      </VoteBtn>
                      <VoteBtn type="button" $on={votes.mine === 'disagree'} aria-pressed={votes.mine === 'disagree'} onClick={() => onVote('disagree')}>
                        <ThumbsDown size={13} /> {votes.down || 0}
                      </VoteBtn>
                    </VoteGroup>
                  )}
                </ValenceRow>
              )}

              <Body>
                <NodeSection>
                  {mode === 'edit' ? (
                    <>
                      <FormCheckOwnIndicator
                        isOwn={isCreator}
                        formCheck={
                          node?.is_scenario_starter
                            ? null
                            : node?.node_analysis?.formCheck
                        }
                        pending={node?.is_scenario_starter ? false : formCheckPending}
                        dismissed={formCheckDismissed}
                        onDismiss={onDismissFormCheck}
                        variant="border"
                        fill
                        size="sm"
                        warnPlacement="above"
                      >
                      <Label>{t('impactPath.nodeModal.titleFieldLabel')}</Label>
                      <input
                        value={editTitle}
                        onChange={(e) => {
                          titleFieldDirtyRef.current = true;
                          setEditTitle(e.target.value);
                        }}
                        placeholder={t('impactPath.nodeModal.thoughtTitlePlaceholder')}
                        maxLength={80}
                        style={{
                          width: '100%', padding: '8px 12px',
                          background: '#1a1a1a', border: '1px solid #444',
                          borderRadius: 4, color: '#fff',
                          fontFamily: "'Rational', sans-serif",
                          fontSize: '1rem', fontWeight: 700,
                          outline: 'none',
                        }}
                      />
                      <Label style={{ marginTop: 12 }}>{t('impactPath.nodeModal.descriptionFieldLabel')}</Label>
                      <div style={{ position: 'relative' }}>
                        <EditTextArea
                          value={editDesc}
                          onChange={(e) => { setEditDesc(e.target.value); if (analysisReady) { setAnalysisReady(false); setAiScores(null); setAiSources([]); } }}
                          placeholder={t('impactPath.nodeModal.thoughtDescPlaceholder')}
                          autoFocus
                        />
                        <FloatingActions>
                          <FloatingActionsGroup>
                            <UndoRedoButtons
                              canUndo={canUndoEdit}
                              canRedo={canRedoEdit}
                              onUndo={undoEdit}
                              onRedo={redoEdit}
                              buttonComponent={Btn}
                              buttonStyle={{
                                padding: '8px 10px',
                                fontSize: '0.78rem',
                                fontWeight: 600,
                                background: '#2a2a2a',
                                border: '1px solid #555',
                                color: '#fff',
                              }}
                            />
                            <TidyTextButton
                              text={editDesc}
                              instanceId={instanceId}
                              onApply={setEditDesc}
                              onBeforeApply={pushEditSnapshot}
                              buttonComponent={Btn}
                              buttonStyle={{
                                padding: '8px 10px',
                                fontSize: '0.78rem',
                                fontWeight: 600,
                                background: '#2a2a2a',
                                border: '1px solid #555',
                                color: '#fff',
                              }}
                            />
                          </FloatingActionsGroup>
                        </FloatingActions>
                      </div>
                      </FormCheckOwnIndicator>
                      {isAnalyzing && (
                        <div style={{
                          display: 'flex', alignItems: 'center', gap: 8,
                          color: 'rgba(255,255,255,0.5)', fontSize: '0.85rem', padding: '12px 0',
                        }}>
                          <Spinner size={16} />
                          {t('impactPath.nodeModal.aiAnalyzingThought')}
                        </div>
                      )}
                      {analysisReady && !node?.is_scenario_starter && (aiScores || aiSources?.length > 0) && (
                        <AIScorePanel evaluation={aiScores} scores={aiScores?.tags ? null : aiScores} sources={aiSources} />
                      )}
                      <ButtonRow>
                        <Btn onClick={handleCancelEdit} disabled={isSaving || isAnalyzing}>{t('impactPath.nodeModal.cancel')}</Btn>
                        <Btn $variant="primary" onClick={handleSaveEdit} disabled={isSaving || isAnalyzing || !editDesc.trim()}>
                          <Save size={16} />
                          {isSaving
                            ? t('impactPath.nodeModal.saving')
                            : isAnalyzing
                              ? t('impactPath.nodeModal.analyzing')
                              : (node?.is_scenario_starter || analysisReady)
                                ? t('common.save')
                                : t('impactPath.nodeModal.saveOrAnalyze')}
                        </Btn>
                      </ButtonRow>
                    </>
                  ) : (
                    <>
                      <FormCheckOwnIndicator
                        isOwn={isCreator}
                        formCheck={
                          node?.is_scenario_starter
                            ? null
                            : node?.node_analysis?.formCheck
                        }
                        pending={node?.is_scenario_starter ? false : formCheckPending}
                        dismissed={formCheckDismissed}
                        onDismiss={onDismissFormCheck}
                        variant="border"
                        fill
                        size="sm"
                        warnPlacement="above"
                      >
                      <NodeTitle>{node?.text || node?.title || t('impactPath.nodeModal.defaultNodeTitle')}</NodeTitle>

                      {/* Description with human/AI coloring */}
                      {(node?.human_text || node?.ai_analysis || descText) && (
                        <NodeDescription
                          $expandable={isTruncated}
                          onClick={() => isTruncated && setDescExpanded(!descExpanded)}
                        >
                          {renderDescriptionDiff(node, displayDesc)}
                        </NodeDescription>
                      )}
                      </FormCheckOwnIndicator>
                      {proposal && <ProposedNodePanel {...proposal} />}

                      {/* Scores + references — not for muuttujat (vague factors). */}
                      {!node?.is_scenario_starter && (nodeEval || nodeLegacyScores || displayRefs.length > 0) ? (
                        <div style={{ marginTop: 16, paddingTop: 12, borderTop: '1px solid #333' }}>
                          <AIScorePanel evaluation={nodeEval} scores={nodeLegacyScores} sources={displayRefs} />
                        </div>
                      ) : !node?.is_scenario_starter && !hasNodeAiAnalysis(node?.node_analysis) && node?.depth > 0 && (
                        <div style={{
                          marginTop: 16, paddingTop: 12, borderTop: '1px solid #333',
                          display: 'flex', alignItems: 'center', gap: 8,
                          color: 'rgba(255,255,255,0.35)', fontSize: '0.78rem',
                        }}>
                          <Spinner size={14} />
                          {t('impactPath.nodeModal.aiAnalyzingThought')}
                        </div>
                      )}
                    </>
                  )}

                </NodeSection>

                {/* Sources from RAG — same open-document behavior as AIScorePanel elsewhere */}
                {mode === 'view' && nodeSources.length > 0 && (
                  <NodeSection>
                    <AIScorePanel
                      scores={null}
                      sources={nodeSources.map((s, i) => ({
                        marker: s.marker || `[${i + 1}]`,
                        citation: s.citation || s.title || t('common.sourceTitleFallback'),
                        title: s.citation || s.title || t('common.sourceTitleFallback'),
                        documentId: s.documentId || s.document_id || null,
                        chunkId: s.chunkId || s.chunk_id || null,
                        excerpt: s.excerpt || null,
                        relevance: s.relevance != null ? s.relevance : null,
                      }))}
                      defaultTab="refs"
                    />
                  </NodeSection>
                )}

                <div style={{ height: 8 }} />

                {mode === 'view' && comments.length > 0 && (
                  <div style={{ paddingTop: 16, borderTop: '1px solid #333' }}>
                    <SectionTitle>{t('impactPath.notes.title')} ({comments.length})</SectionTitle>
                    {comments.map((c) => (
                      <CommentBox key={c.id}>
                        <CommentHeader>
                          <CommentName>{c.participantName || 'Anonymous'}</CommentName>
                          <CommentDate>
                            {new Date(c.createdAt).toLocaleDateString(
                              language === 'en' ? 'en-GB' : language === 'sv' ? 'sv-SE' : 'fi-FI',
                              {
                              month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit'
                            })}
                          </CommentDate>
                        </CommentHeader>
                        <CommentText>{c.text}</CommentText>
                      </CommentBox>
                    ))}
                  </div>
                )}
                {/* Huomiot (§4h): notes about the node, never nodes. The list is
                    v1's; the input is new — v1 accepted onComment and never
                    gave anyone a place to write one. */}
                {mode === 'view' && onComment && (
                  <NoteForm onSubmit={async (e) => {
                    e.preventDefault();
                    const text = noteDraft.trim();
                    if (!text || notePending) return;
                    setNotePending(true);
                    try { await onComment(text); setNoteDraft(''); } finally { setNotePending(false); }
                  }}>
                    <input
                      value={noteDraft}
                      onChange={(e) => setNoteDraft(e.target.value)}
                      placeholder={t('impactPath.notes.placeholder')}
                      maxLength={400}
                      aria-label={t('impactPath.notes.title')}
                    />
                    <button type="submit" disabled={!noteDraft.trim() || notePending}>{t('impactPath.notes.add')}</button>
                  </NoteForm>
                )}
              </Body>

              {mode === 'view' && (
                <BottomBar>
                  <div>
                    {onDelete && (isCreator || isAdmin) && (
                      <DeleteConfirmButton
                        onDelete={handleDelete}
                        deleting={isDeleting}
                        warning={childCount > 0 && !hasOthersChildren ? t('impactPath.nodeModal.deleteWarning', { count: childCount }) : undefined}
                        blockedMessage={hasOthersChildren > 0 ? t('impactPath.nodeModal.deleteBlocked', { count: hasOthersChildren }) : undefined}
                      />
                    )}
                  </div>
                  <div>
                    {onAddConsequence && (
                      <Btn $pad="8px 14px" $fontSize="0.85rem" onClick={onAddConsequence}>
                        <Plus size={14} />
                        {t('impactPath.nodeModal.addImpact')}
                      </Btn>
                    )}
                  </div>
                </BottomBar>
              )}
            </>
          )}
        </ModalBox>
      </Container>
    </>
  );
}
